import React, { useEffect, useRef, useState } from "react";
import "../styles/layout.css";

export default function CustomListing({
  data = [],            // ✅ default empty array
  setData = () => { },   // ✅ default noop
  fetchData,
  columns = [],         // ✅ default empty array
  pageSize = 5,
  postData = {},
  onSelectionChange,
  selectedKeys = [],
  headerTrClass = "",
  headerThClass = "",
  thClass = "",
  tdClass = "",
  tableWidth = "100%",
  tableHeight = "auto",
  paginationClass = "",
  showCheckbox = true,
  refreshTrigger = 0,     // ✅ ADD THIS
}) {
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);

  const loadData = async (pageOffset) => {
    if (pageOffset < 0) return;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchData({ limit: pageSize, offset: pageOffset, ...postData });
      if (requestId !== requestIdRef.current) return; // stale response
      if (result?.success) {
        setData(result.data || []);
        setTotal(result.total || 0);
        setOffset(pageOffset);
      } else {
        setError(result?.message || "Failed to load data");
      }
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      setError(e?.message || "Error loading data");
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  // 🔍 LOCAL FILTERING LOGIC
  const getDisplayedData = () => {
    const st = postData.search?.toLowerCase();
    if (!st) return data;

    return data.filter((item) => {
      // Check serialNumber/macAddress explicitly or check all rendered columns
      const searchStr = [
        item.serialNumber,
        item._id,
        item.macAddress,
        item.manufacturerId,
        ...(columns.map(col => col.render ? col.render(item) : item[col.key]))
      ].join(" ").toLowerCase();

      return searchStr.includes(st);
    });
  };

  const displayedData = fetchData ? data : getDisplayedData();

  useEffect(() => {
    const handler = setTimeout(() => {
      loadData(0);
    }, 300);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(postData), pageSize]);

  // Handle manual refresh while staying on current offset
  useEffect(() => {
    if (refreshTrigger > 0) {
      loadData(offset);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  const totalPages = Math.ceil((total || 0) / pageSize) || 1;

  const isPageFullySelected = displayedData?.length > 0 && displayedData.every((d) => selectedKeys.includes(d._id));

  const getSelectedItems = (updatedIds) => {
    const pageMap = Object.fromEntries(data.map((d) => [d._id, d]));
    return updatedIds.map((id) => pageMap[id] || null).filter(Boolean);
  };

  const handleCheckboxChange = (itemId, checked) => {
    const current = new Set(selectedKeys);
    const addedIds = [];
    const removedIds = [];

    if (checked && !current.has(itemId)) addedIds.push(itemId);
    if (!checked && current.has(itemId)) removedIds.push(itemId);

    checked ? current.add(itemId) : current.delete(itemId);

    onSelectionChange?.({
      addedIds,
      removedIds,
      updatedIds: Array.from(current),
      selectedItems: getSelectedItems(Array.from(current)),
    });
  };

  const handleSelectAll = (checked) => {
    const current = new Set(selectedKeys);
    const pageIds = displayedData.map((d) => d._id);
    const addedIds = checked ? pageIds.filter((id) => !current.has(id)) : [];
    const removedIds = !checked ? pageIds.filter((id) => current.has(id)) : [];

    if (checked) addedIds.forEach((id) => current.add(id));
    if (!checked) removedIds.forEach((id) => current.delete(id));

    onSelectionChange?.({
      addedIds,
      removedIds,
      updatedIds: Array.from(current),
      selectedItems: getSelectedItems(Array.from(current)),
    });
  };

  return (
    <div className="custom-listing-container" style={{ width: tableWidth, height: tableHeight }}>
      {loading && <div className="loading-overlay">Loading...</div>}
      {error && <div className="error-banner">{error}</div>}

      <div className="table-wrapper">
        <table style={{ width: "100%" }}>
          <thead>
            <tr className={headerTrClass}>
              {showCheckbox && (
                <th className={`${headerThClass} ${thClass}`}>
                  <input
                    type="checkbox"
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    checked={isPageFullySelected}
                    className="green-checkbox"
                  />
                </th>
              )}

              <th className={`${headerThClass} ${thClass}`}>S.No</th>

              {columns.map((col) => (
                <th key={col.key} className={`${headerThClass} ${thClass}`}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {displayedData?.length === 0 && !loading ? (
              <tr>
                <td colSpan={columns.length + (showCheckbox ? 2 : 1)} className="no-records">
                  No records found
                </td>
              </tr>
            ) : (
              displayedData.map((item, idx) => (
                <tr key={item._id || idx}>
                  {showCheckbox && (
                    <td className={tdClass}>
                      <input
                        type="checkbox"
                        checked={selectedKeys.includes(item._id)}
                        onChange={(e) => handleCheckboxChange(item._id, e.target.checked)}
                        className="green-checkbox"
                      />
                    </td>
                  )}

                  <td className={tdClass}>{offset * pageSize + idx + 1}</td>

                  {columns.map((col) => (
                    <td key={col.key} className={tdClass}>
                      {col.render
                        ? col.render(item)
                        : typeof item[col.key] === "object" && item[col.key] !== null
                          ? JSON.stringify(item[col.key])
                          : item[col.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>

        </table>
      </div>

      <div className={paginationClass || "pagination-footer"}>
        <button onClick={() => loadData(offset - 1)} disabled={offset === 0}>Previous</button>
        <span>Page {offset + 1} of {totalPages}</span>
        <button onClick={() => loadData(offset + 1)} disabled={offset + 1 >= totalPages}>Next</button>
      </div>
    </div>
  );
}
